import { darken } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import { DEFAULT_MAP_RINGS, MapRing } from '../types/finderConfig';

export const METERS_PER_MILE = 1609.344;

/**
 * Ring color: the org's brand primary for the innermost ring, then a
 * progressively darker tone of that primary for each outer ring so the bands
 * stay on-brand while still reading apart. Darkening is capped so far rings
 * don't collapse to black.
 */
export const ringColor = (index: number, primary: string): string => (
  index === 0 ? primary : darken(primary, Math.min(0.2 * index, 0.7))
);

/**
 * Valid rings only (positive `miles`), sorted by radius. A missing field (older
 * BE) falls back to the defaults; an explicit array is honored as-is, so an
 * empty list means "no rings" and the map draws none.
 */
export const normalizeMapRings = (raw: unknown): MapRing[] => {
  if (!Array.isArray(raw)) return DEFAULT_MAP_RINGS;
  return raw
    .map((entry): MapRing | null => {
      if (!entry || typeof entry !== 'object') return null;
      const miles = Number((entry as Record<string, unknown>).miles);
      if (!Number.isFinite(miles) || miles <= 0) return null;
      return { miles };
    })
    .filter((r): r is MapRing => r !== null)
    .sort((a, b) => a.miles - b.miles);
};

/** Locale-aware number for a ring radius ("1", "1.5", "1,5"), digits included. */
const formatRingMiles = (miles: number, lang: string): string => {
  try {
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(miles);
  } catch {
    return String(miles);
  }
};

/** Ring legend label, auto-formatted "X mile(s)" in the active language. */
export const ringLabel = (ring: MapRing, t: TFunction, lang: string): string => {
  const miles = formatRingMiles(ring.miles, lang);
  const key = ring.miles === 1
    ? 'finder.distance_ring_label_singular'
    : 'finder.distance_ring_label_plural';
  return t(key, { miles });
};
